import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { getLCAPhaseSelection } from "../../../../../apis/Product/LCAPhaseSelectionApi";
import { getLCAResults48Data } from "../../../../../apis/Product/LCAResultsFlow48Api";
import { getProductDetail } from "../../../../../apis/Product";
import LCAPhaseDisplay from "./LCAPhaseDisplay";
import LCAResultsMatrix from "./LCAResultsMatrix";
import "./index.css";

const BR18_PHASES = { A1: true, A2: true, A3: true, A4: true, A5: true, B4: true, B6: true, C3: true, C4: true, D: true };

function detectScope(phases) {
  if (!phases) return "custom";
  const keys = Object.keys(phases).filter((k) => phases[k]);
  if (keys.length === 3 && keys.includes("A1") && keys.includes("A2") && keys.includes("A3")) return "cradle-to-gate";
  if (keys.includes("C4_tech") || keys.includes("C4_bio")) return "cradle-to-cradle";
  const br18Keys = Object.keys(BR18_PHASES);
  if (keys.length === br18Keys.length && br18Keys.every((k) => phases[k])) return "br18";
  if (keys.length === 16 && !keys.includes("D") && !keys.includes("C4_tech") && !keys.includes("C4_bio")) return "cradle-to-grave";
  return "custom";
}

const LCAResultsContent = ({ productId }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const businessId = queryParams.get("business") || localStorage.getItem("id_business");

  const [selectedPhases, setSelectedPhases] = useState({});
  const [lcaResults, setLcaResults] = useState(null);
  const [productInfo, setProductInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [lcaScope, setLcaScope] = useState("custom");

  // Fetch product info for header
  useEffect(() => {
    if (!productId || !businessId) return;
    getProductDetail({
      id: productId,
      business: businessId,
      setProductName: (name) => setProductInfo((prev) => ({ ...prev, product_name: name })),
      setFunctionalUnit: (fu) => setProductInfo((prev) => ({ ...prev, functional_unit: fu })),
      setFunctionalUnitNum: (num) => setProductInfo((prev) => ({ ...prev, fu_amount: num })),
      setFunctionalUnitUnit: (unit) => setProductInfo((prev) => ({ ...prev, fu_unit: unit })),
    });
  }, [productId, businessId]);

  // Fetch selected phases from LCAPhaseSelection
  const fetchPhaseSelection = useCallback(() => {
    if (!productId) return;
    getLCAPhaseSelection({
      productId,
      setData: (data) => {
        if (data && data.lca_phases) {
          setSelectedPhases(data.lca_phases);
          setLcaScope(detectScope(data.lca_phases));
        } else {
          setSelectedPhases({});
          setLcaScope("custom");
        }
      },
    });
  }, [productId]);

  // Fetch LCA results based on selected phases
  const fetchLCAResults = useCallback(() => {
    if (!productId) return;
    setLoading(true);
    getLCAResults48Data({
      productId,
      setObjects: (data) => {
        setLcaResults(data);
        setLoading(false);
      },
    });
  }, [productId]);

  useEffect(() => { fetchPhaseSelection(); }, [fetchPhaseSelection]);
  useEffect(() => { fetchLCAResults(); }, [fetchLCAResults, selectedPhases]);

  // Listen for phase selection changes
  useEffect(() => {
    const handlePhaseChange = () => {
      fetchPhaseSelection();
      fetchLCAResults();
    };
    window.addEventListener("lcaPhaseSelectionChanged", handlePhaseChange);
    return () => { window.removeEventListener("lcaPhaseSelectionChanged", handlePhaseChange); };
  }, [fetchPhaseSelection, fetchLCAResults]);

  if (loading) {
    return (
      <div className="lca-results-loading">
        <div className="text-center">{t("LOADING") || "Loading LCA results..."}</div>
      </div>
    );
  }

  return (
    <div className="lca-results-content-wrapper">
      {/* System Boundary – Read Only Phase Display */}
      <LCAPhaseDisplay selectedPhases={selectedPhases} lcaScope={lcaScope} />

      {/* Results Matrix */}
      <LCAResultsMatrix
        productId={productId}
        selectedPhases={selectedPhases}
        lcaScope={lcaScope}
        lcaResults={lcaResults}
        productInfo={productInfo}
      />
    </div>
  );
};

export default LCAResultsContent;
